/**
 * Keeps phone browser chrome from covering fixed bottom UI (bottom sheets, bottom nav).
 *
 * - `--toolbar-inset` (px): how much of the layout viewport the browser's own bottom
 *   toolbar covers, measured from the visual viewport. 0 when nothing covers it.
 * - `data-ios-browser="1"` on <html>: iPhone/iPad Safari in a normal tab (not an
 *   installed PWA). Its toolbar can float over page content without being reported
 *   anywhere, so CSS adds a little extra scroll room under sheets there.
 *
 * Everything keys off these two hooks in globals.css, scoped to `max-width: 767px`,
 * so desktop is unaffected.
 */
export function initViewportInset(): void {
  if (typeof window === "undefined") return;
  const root = document.documentElement;

  const ua = navigator.userAgent;
  const isIos = /iPhone|iPad|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
  const standalone =
    window.matchMedia?.("(display-mode: standalone)").matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true;
  if (isIos && !standalone) root.setAttribute("data-ios-browser", "1");

  const vv = window.visualViewport;
  if (!vv) return;

  const update = () => {
    const covered = Math.max(0, Math.round(window.innerHeight - (vv.height + vv.offsetTop)));
    // A large value means the on-screen keyboard (handled by resizes-content), not browser chrome.
    root.style.setProperty("--toolbar-inset", covered > 0 && covered < 160 ? `${covered}px` : "0px");
  };
  update();
  vv.addEventListener("resize", update);
  vv.addEventListener("scroll", update);
}
